import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import history from "./history";
import * as URL from "./data/routes-constants";
import PageLoader from "./components/page-loader";
import { useSelector } from "react-redux";
import { getLocalStorage } from "./utils";
import { TABLES_TABS_MAP, USER_ROLES } from "./data/constants";
import { ACCOUNT_NUMBER, AUTH_TOKEN } from "./data/local-storage-constants";
import { isMobileDevice } from "./helpers/device-info-helper";
import OutletStatusPage from "./pages/outlet-status";
import AccountingPage from "./pages/accounting";
import { ACCOUNTING_TABS_MAP } from "./data/accounting-constants";
import { sendPageViewEvent } from "./helpers/ga-helpers";
import { getFilteredTooltipOptions } from "./helpers/common-helper";
import { REPORTS_SUBTABS_MAP } from "./data/reports-constants";
import FinanceInventoryModule from "./features/finance-inventory-module";

const ReportsPage = lazy(() => import('./pages/reports')),
  SingleReportViewPage = lazy(() => import('./pages/single-report-view')),
  CatalogPage = lazy(() => import('./pages/catalog')),
  AddItemPage = lazy(() => import('./pages/add-item')),
  AddItemVariantComponent = lazy(() => import('./pages/add-item-variant')),
  LoginPage = lazy(() => import('./pages/login')),
  DashboardPage = lazy(() => import('./pages/dashboard')),
  RedirectPage = lazy(() => import("./pages/redirect-page")),
  NotFoundPage = lazy(() => import("./pages/not-found")),
  DownloadsPage = lazy(()=>import("./pages/downloads")),
  TablesPage = lazy(() => import('./pages/tables')),
  SupportLoginPage = lazy(() => import ("./pages/support-login"));

export const PrivateRoute = ({ children }) => {
  const { userData } = useSelector((state) => ({
    userData: state.businessReducer.userData
  }));
  const isAuthenticated = !!getLocalStorage(AUTH_TOKEN);
  if (!isAuthenticated) {
    return <Navigate to={URL.LOGIN_PAGE_ROUTE} />;
  }
  if (userData?.role === USER_ROLES.admin && !isMobileDevice()) {
    return children;
  }
  return <Navigate to={URL.NOT_FOUND_ROUTE} />;
}

const AppRoutes = () => {
  const [reportsDefaultSubTab, setReportsDefaultSubTab] = useState("");
  const { allowedFeatures } = useSelector((state) => ({
    allowedFeatures: state.businessReducer?.userData?.allowedFeatures,
  }));
  const businessReducer = useSelector((state) => state.businessReducer);
  let unlistener = useRef(null);

  useEffect(() => {
    unlistener.current = history.listen((location) => {
      sendPageViewEvent({
        page_path: location.location.pathname,
        page_title: window.document.title,
        page_url: window.location.href,
        client_account: getLocalStorage(ACCOUNT_NUMBER) || "",
        client_user_id: businessReducer.userData.userId,
        client_user_role: businessReducer.userData.role,
        client_business_name: businessReducer.businessData.brandName,
      });
    });

    return () => {
      unlistener.current();
    };
  }, []);
  
  useEffect(() => {
    setReportsDefaultSubTab(
      Object.keys(getFilteredTooltipOptions(REPORTS_SUBTABS_MAP))[0]
    );
  }, [allowedFeatures]);

  return (
    <Suspense fallback={<PageLoader show={true} />}>
      <Routes history={history}>
        <Route exact path={'/'} element={<Navigate to={URL.LOGIN_PAGE_ROUTE} />} />
        <Route exact path={URL.LOGIN_PAGE_ROUTE} element={<LoginPage />} />
        <Route exact path={URL.SUPPORT_LOGIN_ROUTE} element={<SupportLoginPage />} />
        <Route exact path={URL.REPORTS_PAGE_ROUTE + "/:subTabId"} element={<ReportsPage />} />
        <Route
          exact path={URL.REPORTS_PAGE_ROUTE}
          element={<Navigate to={URL.REPORTS_PAGE_ROUTE + "/" + reportsDefaultSubTab} />}
        />
        <Route exact path={URL.DASHBOARD_PAGE_ROUTE} element={<DashboardPage />} />
        <Route exact path={URL.REDIRECT_PAGE_ROUTE} element={<RedirectPage />} />
        <Route exact path={URL.DOWNLOADS_PAGE_ROUTE} element={<DownloadsPage />} />
        <Route
          exact path={URL.REPORTS_PAGE_ROUTE + "/:subTabId/:reportGroup/:reportIndex/:reportId"}
          element={<SingleReportViewPage />}
        />
        <Route exact path={URL.CATALOG_PAGE_ROUTE + '/:tabID'} element={
          <PrivateRoute>
            <CatalogPage />
          </PrivateRoute>
        } />
        <Route exact path={URL.CATALOG_PAGE_ROUTE} element={<PrivateRoute>
          <CatalogPage />
        </PrivateRoute>} />
        <Route exact path={URL.ADD_ITEM_PAGE_ROUTE} element={<PrivateRoute>
          <AddItemPage />
        </PrivateRoute>} />
        <Route exact path={URL.ADD_ITEM_VARIANT_PAGE_ROUTE} element={<PrivateRoute>
          <AddItemVariantComponent />
        </PrivateRoute>} />
        <Route exact path={URL.OUTLET_STATUS_PAGE_ROUTE} element={<OutletStatusPage />} />
        <Route exact path={URL.ACCOUNTING_PAGE_ROUTE + '/:subTabId'} element={<AccountingPage />} />
        <Route
          exact path={URL.ACCOUNTING_PAGE_ROUTE}
          element={<Navigate to={URL.ACCOUNTING_PAGE_ROUTE + "/" + ACCOUNTING_TABS_MAP.cash_management.tabId} />}
        />
        <Route exact path={URL.NOT_FOUND_ROUTE} element={<NotFoundPage />} />
        <Route exact path={URL.TABLES_PAGE_ROUTE + '/:subTabId'} element={<TablesPage />} />
        <Route
          exact path={URL.TABLES_PAGE_ROUTE}
          element={<Navigate to={URL.TABLES_PAGE_ROUTE + "/" + TABLES_TABS_MAP.manage_tables.tabId} />}
        />
        <Route
          exact path={URL.FINANCE_INVENTORY_MODULE}
          element={<FinanceInventoryModule/>}
      />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;